import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface AgentConfiguration {
  Id: string;
  ExternalId: string;
  Name: string;
  Description: string | null;
  CategoryId: string;
  ActivityIds: string[];
  NoInvokeModelAfterActivities: string[];
  ActivityMaxUseCount: Record<string, number>;
  // For list view
  ActivitiesCount?: number;
  Category?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AgentConfigurationService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Add request interceptor to dynamically set baseURL and include OAuth token
      this.api.interceptors.request.use((config) => {
        // Dynamically construct baseURL on each request to get current tenantId
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}/agent`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getAgentConfigurations(): Promise<AgentConfiguration[] | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/configuration');
      return response.data;
    } catch (error: any) {
      console.error('Failed to get agent configurations:', error);
      throw error;
    }
  }

  async getAgentConfiguration(id: string): Promise<AgentConfiguration | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/configuration/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get agent configuration:', error);
      throw error;
    }
  }

  async updateAgentConfiguration(agentConfiguration: AgentConfiguration): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/configuration/${agentConfiguration.Id}`, agentConfiguration);
      return response;
    } catch (error: any) {
      console.error('Failed to update agent configuration:', error);
      throw error;
    }
  }

  async createAgentConfiguration(agentConfiguration: AgentConfiguration): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post('/configuration', agentConfiguration);
      return response;
    } catch (error: any) {
      console.error('Failed to create agent configuration:', error);
      throw error;
    }
  }

  async deleteAgentConfiguration(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/configuration/${id}`);
      return response;
    } catch (error: any) {
      console.error('Failed to delete agent configuration:', error);
      throw error;
    }
  }
}
